import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';

import DrLicitaConfirm from 'components/molecules/dr_licita_confirm';
import EditorViewModal from 'components/molecules/editor_view_modal';
import Loader from 'components/atoms/loader';
import { BlockTitle } from 'components/molecules/editor_view_modal/styles';
import Api from 'services/api';
import { ApplicationState } from 'store';
import { DrLicitaState } from 'store/modules/dr_licita_store/types';
import { getDocuments } from 'store/modules/dr_licita_store/actions';
import { MenusBehaviourTypes } from 'store/modules/menus_behaviour_store/types';
import {
  createPeticoesFolder,
  deletePeticoesFile,
  deletePeticoesFolder,
  fetchEquipe,
  fetchPeticoesItems,
  fetchPeticoesShares,
  MembroEquipe,
  movePeticoesItem,
  openPeticaoConversation,
  PeticaoDocumentoView,
  PeticaoItem,
  peticoesFileDownloadUrl,
  PeticoesListing,
  PeticoesShare,
  renamePeticaoDocument,
  renamePeticoesFolder,
  sharePeticoesItem,
  unsharePeticoesItem,
  uploadPeticoesFile,
  viewPeticaoDocument,
} from 'services/actions/dr_licita_peticoes_actions';

import * as Shell from '../DrLicitaChat/styles';
import * as S from './styles';

// Página "Petições" da aplicação Dr. Licita 3.0 (beta): gerenciador das
// peças com pastas, arquivos e compartilhamento com o time (empresa toda
// ou membros específicos — v1: quem recebe vê e exporta; edição segue com
// o dono, via chat).

const MODES = [
  { mode: 'peticao', title: 'Construir Petição', icon: 'edit_note', disabled: false },
  { mode: 'duvidas', title: 'Dúvidas Jurídicas', icon: 'help_outline', disabled: false },
  { mode: 'jurisprudencia', title: 'Jurisprudência Fácil', icon: 'gavel', disabled: false },
];

const rotaDoModo = (mode: string) =>
  mode === 'jurisprudencia' ? '/dr_licita/jurisprudencia' : `/dr_licita/chat?mode=${mode}`;

const ITEM_ICON: Record<PeticaoItem['type'], string> = {
  pasta: 'folder',
  peticao: 'description',
  arquivo: 'insert_drive_file',
};

const processoDoTitulo = (titulo: string): string => {
  const match = titulo?.match(/\d{1,5}\/\d{4}/);
  return match ? match[0] : '—';
};

type ModalState =
  | { kind: 'nova-pasta' }
  | { kind: 'upload' }
  | { kind: 'renomear'; item: PeticaoItem }
  | { kind: 'compartilhar'; item: PeticaoItem }
  | null;

const DrLicitaPeticoes: React.FC = () => {
  const { documents, documents_request } = useSelector<ApplicationState, DrLicitaState>(
    (state) => state.session.dr_licita,
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const [listing, setListing] = useState<PeticoesListing & { folder_mine?: boolean }>({
    folder: null,
    items: [],
  });
  const [scope, setScope] = useState<'todos' | 'mine'>('todos');
  const [modalEditorOpened, setModalEditorOpened] = useState(false);
  const [selectedDocumentID, setSelectedDocumentID] = useState(0);
  const [teamDoc, setTeamDoc] = useState<PeticaoDocumentoView | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [modalValue, setModalValue] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [sending, setSending] = useState(false);
  const [equipe, setEquipe] = useState<MembroEquipe[]>([]);
  const [itemShares, setItemShares] = useState<PeticoesShare[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<{
    titulo: string;
    mensagem: string;
    acao: () => void;
  } | null>(null);
  const [dragOver, setDragOver] = useState<number | 'raiz' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mostrarAviso = (texto: string) => {
    setAviso(texto);
    window.setTimeout(() => setAviso(null), 3500);
  };

  const load = useCallback(
    async (folderId?: number | null, scopeAtual?: 'todos' | 'mine') => {
      setCarregando(true);
      const data = await fetchPeticoesItems(folderId, scopeAtual ?? scope);
      if (data) setListing(data);
      setCarregando(false);
    },
    [scope],
  );

  useEffect(() => {
    document.title = 'Dr Licita - Petições';
    dispatch(getDocuments());
    load();
  }, []);

  // Deep-link do chat: ?document=<id> abre a peça após a lista carregar
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || !documents_request.loaded) return;
    const documentId = Number(new URLSearchParams(window.location.search).get('document'));
    if (documentId > 0 && documents.some((item) => item.id === documentId)) {
      deepLinkHandled.current = true;
      setSelectedDocumentID(documentId);
      setModalEditorOpened(true);
    }
  }, [documents_request.loaded, documents]);

  const recarregar = () => load(listing.folder?.id ?? null);

  const trocarEscopo = (novo: 'todos' | 'mine') => {
    setScope(novo);
    load(null, novo);
  };

  const abrirItem = async (item: PeticaoItem) => {
    if (item.type === 'pasta') {
      load(item.id);
      return;
    }
    if (item.type === 'arquivo') {
      window.open(peticoesFileDownloadUrl(item.id), '_blank');
      return;
    }
    if (item.mine === false) {
      // Peça de colega: visualizador somente leitura via regra de acesso
      const doc = await viewPeticaoDocument(item.id);
      if (doc) setTeamDoc(doc);
      else mostrarAviso('Não foi possível abrir a petição compartilhada.');
      return;
    }
    setSelectedDocumentID(item.id);
    setModalEditorOpened(true);
  };

  const excluirItem = (item: PeticaoItem) => {
    const mensagens = {
      pasta: `Deseja excluir a pasta "${item.name}"? O conteúdo volta para a raiz.`,
      peticao: `Deseja excluir a petição "${item.name}"?`,
      arquivo: `Deseja excluir o arquivo "${item.name}"?`,
    };
    setConfirmacao({
      titulo: 'Excluir',
      mensagem: mensagens[item.type],
      acao: async () => {
        setConfirmacao(null);
        if (item.type === 'pasta') await deletePeticoesFolder(item.id);
        if (item.type === 'arquivo') await deletePeticoesFile(item.id);
        if (item.type === 'peticao') {
          await Api.delete(`/dr_licita/documents/${item.id}`);
          dispatch(getDocuments());
        }
        recarregar();
      },
    });
  };

  const abrirRenomear = (item: PeticaoItem) => {
    setModalValue(item.name);
    setModal({ kind: 'renomear', item });
  };

  // Compartilhar: carrega equipe + shares atuais do item
  const abrirCompartilhar = async (item: PeticaoItem) => {
    setModal({ kind: 'compartilhar', item });
    setItemShares(await fetchPeticoesShares(item.type, item.id));
    setEquipe(await fetchEquipe());
  };

  const compartilharCom = async (item: PeticaoItem, targetConlicitaId: number | null) => {
    const share = await sharePeticoesItem(item.type, item.id, targetConlicitaId);
    if (share) {
      setItemShares(await fetchPeticoesShares(item.type, item.id));
      mostrarAviso(
        targetConlicitaId ? 'Compartilhado com o membro.' : 'Compartilhado com todos da empresa.',
      );
      recarregar();
    }
  };

  const removerShare = async (item: PeticaoItem, shareId: number) => {
    const ok = await unsharePeticoesItem(shareId);
    if (ok) {
      setItemShares(await fetchPeticoesShares(item.type, item.id));
      recarregar();
    }
  };

  // Editar peça = via chat com a IA, sempre (somente peças minhas)
  const editarPeca = async (item: PeticaoItem) => {
    setSending(true);
    const conversa = await openPeticaoConversation(item.id);
    setSending(false);
    if (conversa) {
      history.push(`/dr_licita/chat?chat=${conversa.id}`);
      return;
    }
    abrirRenomear(item);
  };

  const confirmarModal = async () => {
    if (!modal || modal.kind === 'upload' || modal.kind === 'compartilhar') return;
    const valor = modalValue.trim();
    if (!valor) return;
    setSending(true);
    if (modal.kind === 'nova-pasta') {
      await createPeticoesFolder(valor);
    } else if (modal.item.type === 'pasta') {
      await renamePeticoesFolder(modal.item.id, valor);
    } else if (modal.item.type === 'peticao') {
      await renamePeticaoDocument(modal.item.id, valor);
      dispatch(getDocuments());
    }
    setSending(false);
    setModal(null);
    setModalValue('');
    recarregar();
  };

  const enviarArquivos = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setSending(true);
    // Upload sequencial: mantém a ordem e simplifica o tratamento de erro
    // eslint-disable-next-line no-restricted-syntax
    for (const file of Array.from(files)) {
      // eslint-disable-next-line no-await-in-loop
      await uploadPeticoesFile(file, listing.folder?.id ?? null);
    }
    setSending(false);
    setModal(null);
    recarregar();
  };

  const aoSoltar = async (event: React.DragEvent, destino: number | null) => {
    event.preventDefault();
    setDragOver(null);
    try {
      const dado = JSON.parse(event.dataTransfer.getData('application/json'));
      if (dado?.type && dado?.id && dado.type !== 'pasta') {
        await movePeticoesItem(dado.type, dado.id, destino);
        recarregar();
      }
    } catch {
      // drop de algo que não é um item da lista: ignora
    }
  };

  const nomeDoShare = (share: PeticoesShare): string => {
    if (!share.target_conlicita_id) return 'Todos da empresa';
    const membro = equipe.find((m) => m.conlicita_id === share.target_conlicita_id);
    return membro ? membro.name : `Usuário ${share.target_conlicita_id}`;
  };

  const renderCompartilhar = (item: PeticaoItem) => {
    const alvosAtuais = new Set(itemShares.map((share) => share.target_conlicita_id));
    const disponiveis = equipe.filter((membro) => !alvosAtuais.has(membro.conlicita_id));
    return (
      <>
        <S.ShareSectionTitle>Compartilhado</S.ShareSectionTitle>
        {itemShares.length === 0 && <S.ShareEmpty>Ainda não compartilhado.</S.ShareEmpty>}
        {itemShares.map((share) => (
          <S.ShareRow key={share.id}>
            <i className="material-icons">{share.target_conlicita_id ? 'person' : 'groups'}</i>
            {nomeDoShare(share)}
            <button
              type="button"
              title="Remover compartilhamento"
              onClick={() => removerShare(item, share.id)}
            >
              <i className="material-icons">close</i>
            </button>
          </S.ShareRow>
        ))}

        <S.ShareSectionTitle>Adicionar</S.ShareSectionTitle>
        {!alvosAtuais.has(null) && (
          <S.ShareRow>
            <i className="material-icons">groups</i>
            Todos da empresa
            <button
              type="button"
              title="Compartilhar com todos da empresa"
              onClick={() => compartilharCom(item, null)}
            >
              <i className="material-icons">add</i>
            </button>
          </S.ShareRow>
        )}
        {disponiveis.map((membro) => (
          <S.ShareRow key={membro.conlicita_id}>
            <i className="material-icons">person</i>
            {membro.name}
            <button
              type="button"
              title={`Compartilhar com ${membro.name}`}
              onClick={() => compartilharCom(item, membro.conlicita_id)}
            >
              <i className="material-icons">add</i>
            </button>
          </S.ShareRow>
        ))}
        {disponiveis.length === 0 && alvosAtuais.has(null) && (
          <S.ShareEmpty>Todos os membros do time já têm acesso.</S.ShareEmpty>
        )}
      </>
    );
  };

  const renderModal = () => {
    if (!modal) return null;
    const titulos = {
      'nova-pasta': 'Nova Pasta',
      upload: 'Upload de Arquivos',
      renomear: 'Renomear',
      compartilhar: 'Compartilhar',
    };
    const titulo = titulos[modal.kind];
    return (
      <Modal show centered onHide={() => setModal(null)}>
        <S.ModalContainer>
          <div className="modal-header">
            <h5 className="modal-title text-nowrap">
              <strong>{titulo}</strong>
            </h5>
            <button
              aria-label="Close"
              className="close"
              data-dismiss="modal"
              type="button"
              onClick={() => setModal(null)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <S.ModalBody>
            {modal.kind === 'compartilhar' && renderCompartilhar(modal.item)}
            {modal.kind === 'upload' && (
              <S.UploadArea
                type="button"
                dragging={dragOver === 'raiz'}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOver('raiz');
                }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragOver(null);
                  enviarArquivos(event.dataTransfer.files);
                }}
              >
                <i className="material-icons">upload_file</i>
                {sending ? 'Enviando…' : 'Clique para selecionar ou arraste os arquivos aqui'}
              </S.UploadArea>
            )}
            {(modal.kind === 'nova-pasta' || modal.kind === 'renomear') && (
              <label htmlFor="peticoes-modal-input">
                Nome
                <input
                  id="peticoes-modal-input"
                  type="text"
                  value={modalValue}
                  ref={(element) => element?.focus()}
                  onChange={(event) => setModalValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') confirmarModal();
                  }}
                />
              </label>
            )}
          </S.ModalBody>
          {(modal.kind === 'nova-pasta' || modal.kind === 'renomear') && (
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={sending}
                onClick={confirmarModal}
              >
                {modal.kind === 'nova-pasta' ? 'Criar Pasta' : 'Salvar'}
              </button>
            </div>
          )}
        </S.ModalContainer>
      </Modal>
    );
  };

  const pastaSomenteLeitura = Boolean(listing.folder) && listing.folder_mine === false;

  return (
    <Shell.Page>
      <Shell.Sidebar>
        <h1>Dr.Licita</h1>
        <Shell.SidebarScroll>
          <Shell.SidebarButton as={Link} to="/dr_licita/chat">
            <i className="material-icons">add</i>
            Nova conversa
          </Shell.SidebarButton>
          <Shell.SidebarButton active>
            <i className="material-icons">description</i>
            Petições
          </Shell.SidebarButton>
          <Shell.SidebarButton as={Link} to="/dr_licita/projetos">
            <i className="material-icons">folder_open</i>
            Projetos
          </Shell.SidebarButton>
          <Shell.SidebarButton as={Link} to="/dr_licita/biblioteca">
            <i className="material-icons">menu_book</i>
            Biblioteca de Prompts
          </Shell.SidebarButton>

          <h2>Acesso rápido</h2>
          {MODES.map((item) =>
            item.disabled ? (
              <Shell.SidebarButton key={item.mode} disabled>
                <i className="material-icons">{item.icon}</i>
                {item.title}
                <small>em breve</small>
              </Shell.SidebarButton>
            ) : (
              <Shell.SidebarButton key={item.mode} as={Link} to={rotaDoModo(item.mode)}>
                <i className="material-icons">{item.icon}</i>
                {item.title}
              </Shell.SidebarButton>
            ),
          )}
        </Shell.SidebarScroll>

        <Shell.SidebarFooter>
          <button type="button" title="Em breve">
            <i className="material-icons">play_circle_outline</i>
            Veja como funciona
          </button>
        </Shell.SidebarFooter>
      </Shell.Sidebar>

      <Shell.Main>
        <S.Content>
          {listing.folder ? (
            <S.Breadcrumb
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver('raiz');
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(event) => aoSoltar(event, null)}
            >
              <button type="button" title="Voltar para Petições" onClick={() => load(null)}>
                <i className="material-icons">arrow_back</i>
                Petições
              </button>
              <span>&gt; {listing.folder.name}</span>
            </S.Breadcrumb>
          ) : (
            <h2>Petições</h2>
          )}

          <S.Toolbar>
            <select
              value={scope}
              title="Mostrar itens meus ou de todo o time"
              onChange={(event) => trocarEscopo(event.target.value as 'todos' | 'mine')}
            >
              <option value="todos">Por todos</option>
              <option value="mine">Por mim</option>
            </select>
            <span />
            {!pastaSomenteLeitura && (
              <S.ToolbarButton
                type="button"
                disabled={sending}
                onClick={() => setModal({ kind: 'upload' })}
              >
                <i className="material-icons">upload_file</i>
                Upload de Arquivos
              </S.ToolbarButton>
            )}
            {!listing.folder && (
              <S.ToolbarButton
                type="button"
                onClick={() => {
                  setModalValue('');
                  setModal({ kind: 'nova-pasta' });
                }}
              >
                <i className="material-icons">create_new_folder</i>
                Nova Pasta
              </S.ToolbarButton>
            )}
            <S.ToolbarButton as={Link} to="/dr_licita/chat?mode=peticao">
              <i className="material-icons">post_add</i>
              Nova Petição
            </S.ToolbarButton>
          </S.Toolbar>

          <S.Table>
            <S.TableHead>
              <span>Nome</span>
              <span>Processo</span>
              <span>Última Atualização</span>
              <span />
            </S.TableHead>

            {carregando && (
              <S.Empty>
                <Loader />
              </S.Empty>
            )}

            {!carregando &&
              listing.items.map((item) => (
                <S.DropHint
                  key={`${item.type}-${item.id}`}
                  active={dragOver === item.id && item.type === 'pasta' && item.mine !== false}
                >
                  <S.Row
                    draggable={item.type !== 'pasta' && item.mine !== false}
                    onDragStart={(event) => {
                      event.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({ type: item.type, id: item.id }),
                      );
                    }}
                    onDragOver={
                      item.type === 'pasta' && item.mine !== false
                        ? (event) => {
                            event.preventDefault();
                            setDragOver(item.id);
                          }
                        : undefined
                    }
                    onDragLeave={item.type === 'pasta' ? () => setDragOver(null) : undefined}
                    onDrop={
                      item.type === 'pasta' && item.mine !== false
                        ? (event) => aoSoltar(event, item.id)
                        : undefined
                    }
                  >
                    <S.NameCell type="button" onClick={() => abrirItem(item)}>
                      <i className="material-icons">{ITEM_ICON[item.type]}</i>
                      {item.name}
                      {item.mine === false && item.owner && (
                        <S.OwnerTag title={`Compartilhado por ${item.owner}`}>
                          de {item.owner}
                        </S.OwnerTag>
                      )}
                      {item.mine !== false && item.shared && (
                        <S.SharedFlag title="Compartilhado com o time">
                          <i className="material-icons">groups</i>
                        </S.SharedFlag>
                      )}
                    </S.NameCell>
                    <S.Processo>
                      {item.type === 'peticao' ? processoDoTitulo(item.name) : ''}
                    </S.Processo>
                    <S.Data>
                      {moment(item.updated_at).subtract(3, 'hours').format('DD/MM/YYYY')}
                    </S.Data>
                    <S.Actions>
                      {item.type !== 'pasta' ? (
                        <button type="button" title="Visualizar" onClick={() => abrirItem(item)}>
                          <i className="material-icons">visibility</i>
                        </button>
                      ) : (
                        <button type="button" title="Abrir pasta" onClick={() => abrirItem(item)}>
                          <i className="material-icons">folder_open</i>
                        </button>
                      )}
                      {item.mine !== false && (
                        <>
                          <button
                            type="button"
                            title="Compartilhar com o time"
                            onClick={() => abrirCompartilhar(item)}
                          >
                            <i className="material-icons">share</i>
                          </button>
                          {item.type === 'pasta' && (
                            <button
                              type="button"
                              title="Renomear"
                              onClick={() => abrirRenomear(item)}
                            >
                              <i className="material-icons">edit</i>
                            </button>
                          )}
                          {item.type === 'peticao' && (
                            <button
                              type="button"
                              title="Editar no chat com o Dr. Licita"
                              disabled={sending}
                              onClick={() => editarPeca(item)}
                            >
                              <i className="material-icons">edit</i>
                            </button>
                          )}
                          {item.type === 'arquivo' && (
                            <button
                              type="button"
                              title="Renomear (indisponível para arquivos)"
                              disabled
                            >
                              <i className="material-icons">edit</i>
                            </button>
                          )}
                          <button type="button" title="Excluir" onClick={() => excluirItem(item)}>
                            <i className="material-icons">delete_outline</i>
                          </button>
                        </>
                      )}
                    </S.Actions>
                  </S.Row>
                </S.DropHint>
              ))}

            {!carregando && listing.items.length === 0 && (
              <S.Empty>
                {listing.folder
                  ? 'Pasta vazia — arraste petições ou faça upload de arquivos para cá.'
                  : 'Você ainda não possui petições. Crie a primeira pelo chat em “Nova Petição”.'}
              </S.Empty>
            )}
          </S.Table>
        </S.Content>
      </Shell.Main>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
        style={{ display: 'none' }}
        onChange={(event) => {
          enviarArquivos(event.target.files);
          const alvo = event.target;
          alvo.value = '';
        }}
      />

      {confirmacao && (
        <DrLicitaConfirm
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          onCancelar={() => setConfirmacao(null)}
          onConfirmar={confirmacao.acao}
        />
      )}
      {renderModal()}

      {teamDoc && (
        <Modal size="xl" show onHide={() => setTeamDoc(null)}>
          <S.ModalContainer>
            <div className="modal-header">
              <h5 className="modal-title text-nowrap">
                <strong>
                  Visualização da petição{teamDoc.owner ? ` — de ${teamDoc.owner}` : ''}
                </strong>
              </h5>
              <button
                aria-label="Close"
                className="close"
                data-dismiss="modal"
                type="button"
                onClick={() => setTeamDoc(null)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <S.TeamDocBody data-clarity-mask="True">
                <h5 className="text-center mb-4">{teamDoc.title}</h5>
                {teamDoc.blocks.map((block) => (
                  <div key={block.bloc_cd_id}>
                    {block.bloc_tx_title && <BlockTitle>{block.bloc_tx_title}</BlockTitle>}
                    {/* eslint-disable-next-line react/no-danger */}
                    <div dangerouslySetInnerHTML={{ __html: block.bloc_tx_body || '' }} />
                  </div>
                ))}
              </S.TeamDocBody>
            </div>
          </S.ModalContainer>
        </Modal>
      )}

      <S.FeedbackTab
        type="button"
        title="Envie seu feedback sobre o beta"
        onClick={() =>
          dispatch({ type: MenusBehaviourTypes.OPEN_FEEDBACK_MODAL, pathname: 'Dr. Licita' })
        }
      >
        Feedback
      </S.FeedbackTab>

      {aviso && <Shell.Toast>{aviso}</Shell.Toast>}

      <EditorViewModal
        modalEditorOpened={modalEditorOpened}
        setModalEditorOpened={setModalEditorOpened}
        documentTemplate={documents.find((item) => item.id === selectedDocumentID)}
      />
    </Shell.Page>
  );
};

export default DrLicitaPeticoes;
